import { gunzipSync, gzipSync } from "fflate";

import { runQualityChecks } from "@/lib/quality/detector";
import { computeSnapshotStats } from "@/lib/snapshotStats";
import { newId, nowIso } from "@/lib/uid";
import type { CatalogKitConfig } from "@/types";
import type { CatalogSnapshot, NormalizedRow, QualityIssue, SnapshotMeta } from "@/types/schemas";
import { CatalogSnapshotSchema } from "@/types/schemas";

import { logAction } from "./audit";
import { db } from "./schema";

/**
 * Snapshot CRUD. A snapshot is a processed catalog captured at a point in time.
 * The (potentially large) row array is gzip-compressed with fflate before
 * storage and decompressed only on demand, so listing snapshots stays cheap.
 */

/** gzip a `NormalizedRow[]` to bytes for storage. */
export function compressRows(rows: NormalizedRow[]): Uint8Array {
  return gzipSync(new TextEncoder().encode(JSON.stringify(rows)));
}

/** Reverse {@link compressRows}. */
export function decompressRows(bytes: Uint8Array): NormalizedRow[] {
  return JSON.parse(new TextDecoder().decode(gunzipSync(bytes))) as NormalizedRow[];
}

/** Data supplied when capturing a snapshot (rows are compressed here). */
export interface SnapshotInput {
  source_filename: string;
  rows: NormalizedRow[];
  config_snapshot: CatalogKitConfig;
  quality_score: number;
  avg_margin_percent: number;
  total_price_value: number;
  quality_issues?: QualityIssue[];
  categories?: Record<string, number>;
  label?: string;
  notes?: string;
}

/** Options for {@link listSnapshots}. */
export interface ListSnapshotsOptions {
  limit?: number;
  /** Only snapshots uploaded at/after this ISO timestamp. */
  since?: string;
}

function toMeta(snapshot: CatalogSnapshot): SnapshotMeta {
  const clone: Partial<CatalogSnapshot> = { ...snapshot };
  delete clone.rows_compressed;
  return clone as SnapshotMeta;
}

/** Capture a snapshot: compresses rows, stores it, and links it to the profile. */
export async function createSnapshot(
  profileId: string,
  data: SnapshotInput,
): Promise<CatalogSnapshot> {
  const snapshot = CatalogSnapshotSchema.parse({
    id: newId(),
    profile_id: profileId,
    uploaded_at: nowIso(),
    row_count: data.rows.length,
    rows_compressed: compressRows(data.rows),
    source_filename: data.source_filename,
    config_snapshot: data.config_snapshot,
    quality_score: data.quality_score,
    avg_margin_percent: data.avg_margin_percent,
    total_price_value: data.total_price_value,
    quality_issues: data.quality_issues ?? [],
    categories: data.categories ?? {},
    label: data.label,
    notes: data.notes,
  });

  await db.transaction("rw", db.snapshots, db.profiles, async () => {
    await db.snapshots.add(snapshot);
    const profile = await db.profiles.get(profileId);
    if (profile !== undefined) {
      profile.snapshot_ids = [...profile.snapshot_ids, snapshot.id];
      profile.last_used_at = snapshot.uploaded_at;
      await db.profiles.put(profile);
    }
  });

  await logAction({
    profile_id: profileId,
    category: "upload",
    action: `Captured snapshot from "${snapshot.source_filename}"`,
    actor: "user",
    details: { row_count: snapshot.row_count, quality_score: snapshot.quality_score },
  });
  return snapshot;
}

/** Fetch a snapshot record (compressed payload intact), or `null`. */
export async function getSnapshot(id: string): Promise<CatalogSnapshot | null> {
  return (await db.snapshots.get(id)) ?? null;
}

/**
 * List a profile's snapshots as metadata (no row payload), newest first.
 *
 * Note: the compressed payload lives on the same record, so it is read from
 * IndexedDB and then stripped here. Row data is fetched separately via
 * {@link getSnapshotRows}.
 */
export async function listSnapshots(
  profileId: string,
  options: ListSnapshotsOptions = {},
): Promise<SnapshotMeta[]> {
  const { limit, since } = options;
  const snapshots = await db.snapshots.where("profile_id").equals(profileId).sortBy("uploaded_at");
  snapshots.reverse(); // newest first

  let result = since !== undefined ? snapshots.filter((s) => s.uploaded_at >= since) : snapshots;
  if (limit !== undefined) result = result.slice(0, limit);
  return result.map(toMeta);
}

/** Decompress and return a snapshot's rows. Throws if the snapshot is missing. */
export async function getSnapshotRows(id: string): Promise<NormalizedRow[]> {
  const snapshot = await db.snapshots.get(id);
  if (snapshot === undefined) throw new Error(`Snapshot not found: ${id}`);
  return decompressRows(snapshot.rows_compressed);
}

/** Delete a snapshot and unlink it from its profile. */
export async function deleteSnapshot(id: string): Promise<void> {
  const snapshot = await db.snapshots.get(id);
  if (snapshot === undefined) return;

  await db.transaction("rw", db.snapshots, db.profiles, async () => {
    await db.snapshots.delete(id);
    const profile = await db.profiles.get(snapshot.profile_id);
    if (profile !== undefined) {
      profile.snapshot_ids = profile.snapshot_ids.filter((sid) => sid !== id);
      await db.profiles.put(profile);
    }
  });

  await logAction({
    profile_id: snapshot.profile_id,
    category: "delete",
    action: `Deleted snapshot from "${snapshot.source_filename}"`,
    actor: "user",
    details: { snapshot_id: id },
  });
}

/**
 * Update a snapshot's user-facing label (its timestamp is always preserved).
 * An empty label clears it (falls back to the source filename in the UI).
 * Audit-logged.
 */
export async function renameSnapshot(id: string, label: string): Promise<SnapshotMeta> {
  const snapshot = await db.snapshots.get(id);
  if (snapshot === undefined) throw new Error(`Snapshot not found: ${id}`);

  const trimmed = label.trim();
  snapshot.label = trimmed === "" ? undefined : trimmed;
  await db.snapshots.put(snapshot);

  await logAction({
    profile_id: snapshot.profile_id,
    category: "profile_change",
    action: `Renamed snapshot to "${snapshot.label ?? "(untitled)"}"`,
    actor: "user",
    details: { snapshot_id: id },
  });
  return toMeta(snapshot);
}

/**
 * Delete the single oldest snapshot across all profiles (used to free space
 * when storage is near quota). Returns the deleted id, or `null` if none exist.
 */
export async function deleteOldestSnapshot(): Promise<string | null> {
  const oldest = (await db.snapshots.orderBy("uploaded_at").limit(1).toArray())[0];
  if (oldest === undefined) return null;
  await deleteSnapshot(oldest.id);
  return oldest.id;
}

/** Metadata for the single oldest snapshot across all profiles, or `null`. */
export async function getOldestSnapshotMeta(): Promise<SnapshotMeta | null> {
  const oldest = (await db.snapshots.orderBy("uploaded_at").limit(1).toArray())[0];
  return oldest === undefined ? null : toMeta(oldest);
}

/**
 * Replace a snapshot's rows (e.g. after a bulk edit). Re-runs quality checks with
 * the snapshot's config, recomputes stats, recompresses, and audit-logs. The
 * `uploaded_at` timestamp is preserved.
 */
export async function updateSnapshotRows(
  id: string,
  rows: NormalizedRow[],
  action = "Bulk edit applied",
): Promise<CatalogSnapshot> {
  const snapshot = await db.snapshots.get(id);
  if (snapshot === undefined) throw new Error(`Snapshot not found: ${id}`);

  const issues = runQualityChecks(rows, snapshot.config_snapshot);
  const stats = computeSnapshotStats({
    totalRows: rows.length,
    keptRows: rows.length,
    droppedRows: 0,
    rows,
    issues,
  });

  snapshot.rows_compressed = new Uint8Array(compressRows(rows));
  snapshot.row_count = rows.length;
  snapshot.quality_issues = issues;
  snapshot.quality_score = stats.quality_score;
  snapshot.avg_margin_percent = stats.avg_margin_percent;
  snapshot.total_price_value = stats.total_price_value;
  snapshot.categories = stats.categories;
  await db.snapshots.put(snapshot);

  await logAction({
    profile_id: snapshot.profile_id,
    category: "bulk_edit",
    action,
    actor: "user",
    details: { snapshot_id: id, row_count: rows.length },
  });
  return snapshot;
}

/**
 * Apply a profile's retention policy, deleting snapshots older than
 * `retention_days`. A `null`/absent policy means unlimited retention (no-op).
 *
 * Returns:
 *     The number of snapshots deleted.
 */
export async function pruneByRetentionPolicy(profileId: string): Promise<{ deleted: number }> {
  const profile = await db.profiles.get(profileId);
  if (profile === undefined || profile.retention_days == null) return { deleted: 0 };

  const cutoff = new Date(
    Date.now() - profile.retention_days * 24 * 60 * 60 * 1000,
  ).toISOString();

  const stale = await db.snapshots
    .where("profile_id")
    .equals(profileId)
    .filter((s) => s.uploaded_at < cutoff)
    .primaryKeys();

  if (stale.length === 0) return { deleted: 0 };

  await db.transaction("rw", db.snapshots, db.profiles, async () => {
    await db.snapshots.bulkDelete(stale);
    const staleSet = new Set(stale);
    const fresh = await db.profiles.get(profileId);
    if (fresh !== undefined) {
      fresh.snapshot_ids = fresh.snapshot_ids.filter((sid) => !staleSet.has(sid));
      await db.profiles.put(fresh);
    }
  });

  await logAction({
    profile_id: profileId,
    category: "delete",
    action: `Pruned ${stale.length} snapshot(s) past retention (${profile.retention_days}d)`,
    actor: "user",
    details: { deleted: stale.length, retention_days: profile.retention_days },
  });
  return { deleted: stale.length };
}
